// Copyright 2026, Imprivata, Inc.  All rights reserved.

import { Entitlement } from '@api/constants';
import { SortOrder } from '@api/types';
import { hasEntitlement } from '@api/utils';
import { useSession } from '@common/authorization/context/Session';
import { withRole } from '@common/authorization/Roles';
import { useErrorContext } from '@errorHandler/context/ErrorContext';
import { type ApiError, AppError } from '@errorHandler/errors';
import { Button, InputSearch, Select } from '@imprivata-cloud/design-system';
import { downloadAsFileToUserComputer } from '@utils/utils';
import { Spin, Table } from 'antd';
import { LogOut, Settings, Waypoints } from 'lucide-react';
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { FILES_DOWNLOAD_NAMES, ROLE_TYPE_ADMIN } from '../../constants';
import { useAgentVersionReport } from '../identityUsers/hooks/useReport';
import { AgentInstallerDrawer } from './drawers/AgentInstallerDrawer';
import { EndpointBehaviorDrawer } from './drawers/EndpointBehaviorDrawer';
import { buildColumns } from './EndpointsColumns';
import { EndpointsEmpty, EndpointsNoResults } from './empty/EndpointsEmptyStates';
import { useEndpointsList } from './hooks/useEndpointsList';
import { toEndpointRow } from './utils/toEndpointRow';
import './Endpoints.less';

const TRANSLATION_ROOT = 'portal.settings.endpoints';
const SEARCH_DEBOUNCE_MS = 300;
const PAGE_SIZE = 100;
const HOSTNAME_FIELD = 'hostname';
const CREATED_AT_FIELD = 'created_at';
const VERSION_FIELD = 'version';

type SortKey =
  | 'name'
  | 'most-up-to-date'
  | 'least-up-to-date'
  | 'most-recently-installed'
  | 'oldest-installed';

const SORT_FIELD_MAP: Record<SortKey, { field: string; order: SortOrder }> = {
  'name':                   { field: HOSTNAME_FIELD,   order: SortOrder.ASC },
  'most-up-to-date':        { field: VERSION_FIELD,    order: SortOrder.DESC },
  'least-up-to-date':       { field: VERSION_FIELD,    order: SortOrder.ASC },
  'most-recently-installed':{ field: CREATED_AT_FIELD, order: SortOrder.DESC },
  'oldest-installed':       { field: CREATED_AT_FIELD, order: SortOrder.ASC },
};

type DrawerKind = 'installer' | 'behavior' | null;

function EndpointsSection() {
  const { t } = useTranslation();
  const { emitError } = useErrorContext();

  const [searchInput, setSearchInput] = useState('');
  const [searchPattern, setSearchPattern] = useState('');
  const [sortKey, setSortKey] = useState<SortKey>('name');
  const [openDrawer, setOpenDrawer] = useState<DrawerKind>(null);
  const searchTimer = useRef<ReturnType<typeof setTimeout>>();

  const {
    endpointDevices,
    totalCount,
    isLoading,
    isError,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useEndpointsList({
    searchPattern,
    sortField: SORT_FIELD_MAP[sortKey],
    pageSize: PAGE_SIZE,
  });

  useEffect(() => {
    if (isError && error) {
      emitError(new AppError(error));
    }
  }, [isError, error, emitError]);

  useEffect(() => () => clearTimeout(searchTimer.current), []);

  const fallbackLabel = t(`${TRANSLATION_ROOT}.fallback.unregistered`);
  const rows = useMemo(
    () =>
      endpointDevices.map(device =>
        toEndpointRow(device, fallbackLabel, date =>
          t(`${TRANSLATION_ROOT}.columns.installed-date`, { date }),
        ),
      ),
    [endpointDevices, fallbackLabel, t],
  );

  const handleSearchChange = (value: string) => {
    setSearchInput(value);
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => {
      setSearchPattern(value.trim());
    }, SEARCH_DEBOUNCE_MS);
  };

  // Infinite scroll (am-portal standard; mirrors Users): fetch the next page as
  // a sentinel below the list scrolls into view.
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const node = sentinelRef.current;
    if (!node) {
      return undefined;
    }
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && hasNextPage && !isFetchingNextPage) {
          void fetchNextPage();
        }
      },
      { rootMargin: '400px' },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [fetchNextPage, hasNextPage, isFetchingNextPage]);

  // Reuse the existing endpoint-data report pipeline for export (REQ-050) — no new export logic.
  const { mutateAsync: exportEndpoints, isPending: isExporting } = useAgentVersionReport();
  const handleExport = async () => {
    try {
      const blob = await exportEndpoints();
      downloadAsFileToUserComputer(blob, FILES_DOWNLOAD_NAMES.ENDPOINTS_REPORT);
    } catch (e) {
      emitError(new AppError(e as ApiError));
    }
  };

  const hasActiveSearch = searchPattern.length > 0;
  const isEmpty = !isLoading && !isError && rows.length === 0;

  const [headerEl, setHeaderEl] = useState<Element | null>(null);
  useLayoutEffect(() => {
    const header = document.querySelector('.settings-header');
    if (!header) {
      return undefined;
    }
    const container = document.createElement('div');
    container.className = 'endpoints-header-portal';
    header.appendChild(container);
    setHeaderEl(container);
    return () => {
      container.remove();
      setHeaderEl(null);
    };
  }, []);

  const sortItems: { value: SortKey; label: string }[] = [
    { value: 'name',                    label: t(`${TRANSLATION_ROOT}.sort.by-name`) },
    { value: 'most-up-to-date',         label: t(`${TRANSLATION_ROOT}.sort.most-up-to-date`) },
    { value: 'least-up-to-date',        label: t(`${TRANSLATION_ROOT}.sort.least-up-to-date`) },
    { value: 'most-recently-installed', label: t(`${TRANSLATION_ROOT}.sort.most-recently-installed`) },
    { value: 'oldest-installed',        label: t(`${TRANSLATION_ROOT}.sort.oldest-installed`) },
  ];

  return (
    <>
      {headerEl &&
        createPortal(
          <div className="endpoints-header-actions">
            <Button
              variant="default"
              icon={<Settings size={16} />}
              onClick={() => setOpenDrawer('installer')}
            >
              {t(`${TRANSLATION_ROOT}.actions.agent-settings`)}
            </Button>
            <Button
              variant="default"
              icon={<Waypoints size={16} />}
              onClick={() => setOpenDrawer('behavior')}
            >
              {t(`${TRANSLATION_ROOT}.actions.endpoint-behavior`)}
            </Button>
            <Button
              variant="default"
              icon={<LogOut size={16} />}
              disabled={isExporting}
              onClick={handleExport}
            >
              {t(`${TRANSLATION_ROOT}.actions.export`)}
            </Button>
          </div>,
          headerEl,
        )}

      <div className="endpoints-container">
        <div className="endpoints-toolbar">
          <InputSearch
            className="endpoints-search"
            aria-label={t(`${TRANSLATION_ROOT}.search-placeholder`)}
            placeholder={t(`${TRANSLATION_ROOT}.search-placeholder`)}
            value={searchInput}
            onChange={e => handleSearchChange(e.target.value)}
          />
          <div className="endpoints-toolbar-controls">
            <div className="endpoints-sort">
              <Select
                aria-label={t(`${TRANSLATION_ROOT}.sort.label`)}
                items={sortItems}
                value={sortKey}
                onSelectionChange={value => setSortKey(value as SortKey)}
              />
            </div>
            <span className="endpoints-count">
              {t(`${TRANSLATION_ROOT}.count`, { count: totalCount })}
            </span>
          </div>
        </div>

        {isEmpty && hasActiveSearch && <EndpointsNoResults />}
        {isEmpty && !hasActiveSearch && (
          <EndpointsEmpty onOpenSettings={() => setOpenDrawer('installer')} />
        )}
        {!isEmpty && (
          <>
            <Table<ReturnType<typeof toEndpointRow>>
              className="endpoints-table"
              dataSource={rows}
              rowKey="rowKey"
              loading={isLoading}
              pagination={false}
              showHeader={false}
              columns={buildColumns(t)}
            />
            <div ref={sentinelRef} className="endpoints-scroll-sentinel">
              {isFetchingNextPage && <Spin size="small" />}
            </div>
          </>
        )}
      </div>

      {/* Drawer open/close is local state, so dismissing never touches list state (REQ-036/042). */}
      <AgentInstallerDrawer
        open={openDrawer === 'installer'}
        onClose={() => setOpenDrawer(null)}
      />
      <EndpointBehaviorDrawer
        open={openDrawer === 'behavior'}
        onClose={() => setOpenDrawer(null)}
      />
    </>
  );
}

function EndpointsGate() {
  const { tenantInfo } = useSession();
  if (!hasEntitlement(tenantInfo, Entitlement.SSO)) {
    return null;
  }
  return <EndpointsSection />;
}

export default withRole({ authorizedRoles: [ROLE_TYPE_ADMIN] })(EndpointsGate);
